import { appPath } from "../appPath";
export const PLATFORM_TOKEN_KEY = "platform_admin_token";

export type PlatformPermission =
  | "PROPERTY_ORDERS"
  | "EXCEPTION_ORDERS"
  | "SUPPORT"
  | "BILLING_RECONCILIATION"
  | "LAWYER_IDENTITY_REVIEWS"
  | "PERSONAL_LAWYERS"
  | "PAYMENT_COLLECTIONS";

export interface PlatformIdentity {
  id: string;
  username: string;
  name: string;
  mobile: string;
  kind: "OWNER" | "SUBACCOUNT";
  is_agent: boolean;
  permissions: PlatformPermission[];
}

export interface PlatformSubaccount {
  id: string;
  account: string;
  name: string;
  mobile: string;
  status: "ACTIVE" | "DISABLED";
  is_agent: boolean;
  created_at: string;
  permissions: PlatformPermission[];
}

export interface PlatformSubaccountPage {
  items: PlatformSubaccount[];
  total: number;
  page: number;
  page_size: number;
}

export type LawyerVerificationStatus = "NOT_SUBMITTED" | "PENDING" | "APPROVED" | "REJECTED";

export interface LawyerVerificationListItem {
  id: string;
  lawyer_number: string;
  lawyer_name: string;
  mobile: string;
  law_firm_name: string | null;
  status: LawyerVerificationStatus;
  submitted_at: string | null;
  reviewed_at: string | null;
}

export interface LawyerVerificationPage {
  items: LawyerVerificationListItem[];
  total: number;
  page: number;
  page_size: number;
}

export interface LawyerVerificationMaterial {
  id: string;
  kind: string;
  name: string;
  content_type: string;
  review_url: string;
  original_url: null;
}

export interface LawyerVerificationHistory {
  action: "APPROVE" | "REJECT";
  operator_name: string;
  reason: string | null;
  created_at: string;
}

export interface LawyerVerificationDetail extends LawyerVerificationListItem {
  identity_number: string;
  practice_certificate_number: string;
  rejection_reason: string | null;
  materials: LawyerVerificationMaterial[];
  history: LawyerVerificationHistory[];
}

export interface LawyerVerificationDecision {
  id: string;
  status: LawyerVerificationStatus;
  rejection_reason: string | null;
  reviewed_at: string | null;
}

export type PersonalLawyerAccountStatus = "ACTIVE" | "DISABLED";

export interface PersonalLawyerAdminListItem {
  id: string;
  lawyer_number: string;
  lawyer_name: string;
  mobile: string;
  law_firm_name: string | null;
  verification_status: LawyerVerificationStatus;
  account_status: PersonalLawyerAccountStatus;
  registered_at: string;
}

export interface PersonalLawyerAdminPage {
  items: PersonalLawyerAdminListItem[];
  total: number;
  page: number;
  page_size: number;
}

export interface PersonalLawyerAdminProduct {
  product_code: string;
  product_name: string;
  module_name: string;
  status: InstitutionProductStatus;
  personal_price: string | null;
}

export interface PersonalLawyerAdminAudit {
  action: string;
  operator_name: string;
  details: Record<string, unknown>;
  created_at: string;
}

export interface PersonalLawyerAdminDetail extends PersonalLawyerAdminListItem {
  recharge_balance: string;
  reward_balance: string;
  products: PersonalLawyerAdminProduct[];
  audits: PersonalLawyerAdminAudit[];
}

export interface PlatformCredentials {
  account: string;
  password: string;
}

export interface CreatedPlatformSubaccount {
  user: PlatformSubaccount;
  account: string;
  initial_password: string;
}

export type PaymentCollectionChannel = "WECHAT" | "ALIPAY";
export type PaymentCollectionStatus = "PENDING" | "PAID" | "FAILED" | "CLOSED";

export interface PaymentCollectionSummary {
  total_amount: string;
  total_count: number;
  today_amount: string;
  today_count: number;
  month_amount: string;
  month_count: number;
  pending_count: number;
  paid_count: number;
  failed_count: number;
  closed_count: number;
}

export interface PaymentCollectionItem {
  id: string;
  lawyer_account_id: string;
  lawyer_name: string;
  lawyer_number: string;
  reference: string;
  channel: PaymentCollectionChannel;
  amount: string;
  status: PaymentCollectionStatus;
  provider_transaction_id: string | null;
  created_at: string;
  paid_at: string | null;
}

export interface PaymentCollectionDetail extends PaymentCollectionItem {
  provider_prepayment_id: string | null;
  failure_summary: string | null;
  updated_at: string;
}

export interface PaymentCollectionPage {
  items: PaymentCollectionItem[];
  total: number;
  page: number;
  page_size: number;
}

export interface PaymentCollectionReadiness {
  enabled: boolean;
  wechat_enabled: boolean;
  alipay_enabled: boolean;
  wechat_ready: boolean;
  alipay_ready: boolean;
  masked_merchant_id: string | null;
  missing_wechat: string[];
  missing_alipay: string[];
}

export type CollectionAccountType = "PERSONAL_LAWYER" | "INSTITUTION";
export type CollectionMethod = PaymentCollectionChannel | "MANUAL";

export interface CollectionEntry {
  id: string;
  account_type: CollectionAccountType;
  account_id: string;
  account_name: string;
  account_number: string;
  reference: string;
  method: CollectionMethod;
  amount: string;
  received_at: string;
  operator_name: string;
  note: string | null;
}

export interface CollectionEntryPage {
  items: CollectionEntry[];
  total: number;
  page: number;
  page_size: number;
}

export type InstitutionType = "LAW_FIRM" | "FINANCIAL_INSTITUTION" | "OTHER";
export type InstitutionStatus = "ACTIVE" | "DISABLED";
export type InstitutionAgentBinding = "BOUND" | "UNBOUND";

export interface AgentSummary {
  id: string;
  name: string;
  mobile: string;
}

export type InstitutionAgent = AgentSummary;

export interface Institution {
  id: string;
  code: string;
  name: string;
  credit_code: string;
  organization_type: InstitutionType;
  administrator_id: string;
  administrator_name: string;
  administrator_mobile: string;
  balance: string;
  status: InstitutionStatus;
  authorization_material_required: boolean;
  created_at: string;
  agent: InstitutionAgent | null;
}

export interface InstitutionPage {
  items: Institution[];
  total: number;
  page: number;
  page_size: number;
}

export interface InstitutionCredentials {
  account: string;
  password: string;
}

export interface CreateInstitutionInput {
  organization_type: InstitutionType;
  name: string;
  credit_code: string;
  administrator_name: string;
  administrator_mobile: string;
  agent_platform_user_id?: string | null;
  authorization_material_required: boolean;
}

export interface UpdateInstitutionInput {
  name: string;
  credit_code: string;
  administrator_name: string;
  administrator_mobile: string;
  authorization_material_required?: boolean;
}

export interface CreatedInstitution {
  institution: Institution;
  account: string;
  initial_password: string;
}

export interface InstitutionRecharge {
  id: string;
  balance_before: string;
  amount: string;
  balance_after: string;
  reference: string;
  operator_name: string;
  note: string | null;
  created_at: string;
}

export interface InstitutionRechargePage {
  items: InstitutionRecharge[];
  total: number;
  page: number;
  page_size: number;
}

export type InstitutionProductStatus = "OPEN" | "VISIBLE_DISABLED" | "HIDDEN";

export interface InstitutionProductSupplier {
  supplier_product_code?: string | null;
  document_version?: string | null;
  document_reference?: string | null;
  supplier_name: string | null;
  data_source_name: string | null;
  interface_code: string | null;
  interface_name: string | null;
  global_cost: string | null;
  supplier_billing_mode: string | null;
  cost_editable: boolean;
}

export interface InstitutionProduct {
  product_code: string;
  product_name: string;
  service_module_code: string;
  service_module_name: string;
  delivery_mode: "AUTOMATED" | "MANUAL";
  customer_billing_mode: string | null;
  suggested_price: string | null;
  institution_price: string | null;
  agent_commission: string | null;
  status: InstitutionProductStatus;
  suppliers: InstitutionProductSupplier[];
}

export interface InstitutionProductPage {
  items: InstitutionProduct[];
  total: number;
  page: number;
  page_size: number;
}

export interface InstitutionProductChange {
  product_code: string;
  status: InstitutionProductStatus;
  institution_price: string | null;
  agent_commission: string | null;
  interface_costs: Record<string, string | null>;
  manual_supplier_cost?: string | null;
  manual_supplier_billing_mode?: string | null;
}

export interface InstitutionProductDiff {
  opened: Array<Record<string, unknown>>;
  closed_or_hidden: Array<Record<string, unknown>>;
  price_changes: Array<Record<string, unknown>>;
  agent_commission_changes: Array<Record<string, unknown>>;
  global_cost_changes: Array<Record<string, unknown>>;
  manual_supplier_changes: Array<Record<string, unknown>>;
}

export interface InstitutionProductPreview {
  preview_token: string;
  diff: InstitutionProductDiff;
}

export interface PropertyReport {
  type: "property";
  subjectType?: "person" | "enterprise";
  title: string;
  subject: string;
  subjectDetail: string;
  status: string;
  propertyCount: string;
  sections: Array<{ title: string; items: Array<[string, string]> }>;
}

export interface PropertyOrder {
  id: string;
  order_number: string;
  product_code: "PROPERTY" | "PROPERTY_ENTERPRISE";
  institution_name: string;
  subject_type: "person" | "enterprise";
  subject_name: string;
  subject_identifier: string;
  subject_identifier_label: string;
  status: "PENDING_MANUAL" | "COMPLETED";
  created_at: string;
}

export interface PropertyOrderPage {
  items: PropertyOrder[];
  total: number;
  page: number;
  page_size: number;
}

export interface PropertyOrderQuery {
  bucket: "pending" | "completed";
  query?: string;
  page?: number;
  pageSize?: number;
}

export interface PropertyUpload {
  id: string;
  order_id: string;
  original_name: string;
  parse_status: "PARSED";
  property_count: number;
  created_at: string;
  confirmed_at: string | null;
}

export interface PropertyResultVersion {
  result_version: number;
  status: "COMPLETED";
}

export interface PropertyOrderHistoryVersion {
  id: string;
  version_no: number;
  content: PropertyReport;
  change_summary: string | null;
  created_at: string;
}

export interface PropertyOrderHistory {
  uploads: Array<Record<string, unknown>>;
  audits: Array<Record<string, unknown>>;
  versions: PropertyOrderHistoryVersion[];
}

export interface ExceptionOrder {
  order_id: string;
  order_number: string;
  result_number: string | null;
  institution_name: string;
  product_code:
    | "NATURAL_PERSON"
    | "ENTERPRISE_OPERATION"
    | "OWNED_VEHICLE"
    | "VEHICLE_DETAIL";
  product_name: string;
  subject_summary: string;
  order_status: string;
  unresolved_count: number;
  exception_count: number;
  created_at: string;
}

export interface ExceptionOrderPage {
  items: ExceptionOrder[];
  total: number;
  page: number;
  page_size: number;
}

export interface UnifiedExceptionCase {
  id: string;
  source_kind: string;
  source_id: string;
  source_number: string;
  institution_name: string;
  product_code: string;
  product_name: string;
  subject_summary: string;
  reason_summary: string;
  status: "OPEN" | "PROCESSING" | "RESOLVED";
  occurred_at: string;
}

export interface UnifiedExceptionCasePage {
  items: UnifiedExceptionCase[];
  total: number;
  page: number;
  page_size: number;
}

export interface ExceptionCallHistory {
  id: string;
  batch_no: number;
  attempt: number;
  status: string;
  is_supplement: boolean;
  is_billed: boolean | null;
  provider_request_id: string | null;
  error_summary: string | null;
  created_at: string;
  error_code: string | null;
  duration_ms: number | null;
  started_at: string | null;
  finished_at: string | null;
  evidence: {
    available: boolean;
    reason: string | null;
    http_status: number | null;
    response_received?: boolean;
    endpoint_path?: string | null;
    provider_request_id?: string | null;
    duration_ms?: number | null;
    response_body: unknown;
  };
}

export interface ExceptionInterface {
  interface_code: string;
  interface_name: string;
  provider_code: string;
  provider_name: string;
  status: string;
  task_id: string | null;
  error_summary: string | null;
  error_code: string | null;
  resolution_kind: string;
  supplement_count: number;
  supplement_limit: number;
  can_requery: boolean;
  requires_timeout_confirmation: boolean;
  can_confirm_no_data: boolean;
  history: ExceptionCallHistory[];
}

export interface ExceptionOrderDetail extends ExceptionOrder {
  status: string;
  customer_billing_status: string;
  interfaces: ExceptionInterface[];
  audits: Array<{
    action: string;
    note: string | null;
    details: Record<string, unknown>;
    created_at: string;
  }>;
}

export interface ExceptionActionResult {
  status: string;
  message: string;
}

export type SupportTicketStatus = "PENDING" | "PROCESSING" | "RESOLVED";

export interface PlatformSupportAttachment {
  id: string;
  name: string;
  content_type: string;
  url: string;
}

export interface PlatformSupportMessage {
  id: string;
  sender: "CUSTOMER" | "PLATFORM";
  sender_name: string;
  body: string;
  created_at: string;
  attachments: PlatformSupportAttachment[];
}

export interface PlatformSupportTicket {
  id: string;
  order_id: string | null;
  order_number: string | null;
  institution_name: string;
  submitter_name: string;
  product_name: string | null;
  subject_summary: string | null;
  question_summary: string;
  status: SupportTicketStatus;
  message_count: number;
  created_at: string;
  latest_message_at: string;
}

export interface PlatformSupportDetail extends PlatformSupportTicket {
  messages: PlatformSupportMessage[];
  closed_at: string | null;
}

export interface PlatformSupportPage {
  items: PlatformSupportTicket[];
  total: number;
  page: number;
  page_size: number;
}

export type ReconciliationDimension = "institution" | "personal_lawyer" | "supplier";

export interface BillingReconciliationQuery {
  dimension: ReconciliationDimension;
  subject: string;
  dateFrom: string;
  dateTo: string;
  productCode?: string;
  page?: number;
}

export interface BillingReconciliationOption {
  key: string;
  label: string;
}

export interface BillingReconciliationOptions {
  subjects: BillingReconciliationOption[];
  products: BillingReconciliationOption[];
}

export interface BillingReconciliationLine {
  billed_at: string;
  order_id: string;
  subject_summary: string;
  product_code: string;
  product_name: string;
  billing_mode: string;
  payment_method: string | null;
  amount: string | null;
  billing_status: string;
  supplier_cost_status: string | null;
}

export interface BillingReconciliationPage {
  summary: {
    subject_label: string;
    billed_count: number;
    amount: string;
  };
  items: BillingReconciliationLine[];
  total: number;
  page: number;
  page_size: 10;
}

export interface AgentReconciliationQuery {
  agentPlatformUserId?: string;
  dateFrom: string;
  dateTo: string;
  productCode?: string;
  page?: number;
}

export interface AgentReconciliationOption { key: string; label: string; }
export interface AgentReconciliationOptions {
  agent: AgentReconciliationOption | null;
  agents: AgentReconciliationOption[];
  products: AgentReconciliationOption[];
}
export interface AgentReconciliationLine {
  billed_at: string; order_id: string; institution_name: string; product_name: string;
  payment_method: string; customer_amount: string; commission_amount: string; billing_status: string;
}
export interface AgentReconciliationPage {
  summary: { agent_id: string; agent_name: string; institution_count: number; billed_count: number; customer_amount: string; commission_amount: string; };
  items: AgentReconciliationLine[]; total: number; page: number; page_size: 10;
}

export function hasPlatformToken(): boolean {
  return typeof sessionStorage !== "undefined" && Boolean(sessionStorage.getItem(PLATFORM_TOKEN_KEY));
}

export function storePlatformToken(token: string): void {
  sessionStorage.setItem(PLATFORM_TOKEN_KEY, token);
}

export function clearPlatformToken(): void {
  sessionStorage.removeItem(PLATFORM_TOKEN_KEY);
}

async function platformRequest<T>(path: string, init?: RequestInit): Promise<T> {
  const token = typeof sessionStorage === "undefined" ? null : sessionStorage.getItem(PLATFORM_TOKEN_KEY);
  const isFormData = typeof FormData !== "undefined" && init?.body instanceof FormData;
  const response = await fetch(appPath(path), {
    ...init,
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(init?.body && !isFormData ? { "Content-Type": "application/json" } : {}),
      ...init?.headers,
    },
  });
  if (response.status === 401) clearPlatformToken();
  const payload = response.status === 204 ? null : await response.json();
  if (!response.ok) {
    const code = payload?.detail?.code;
    const detailMessage = payload?.detail?.message;
    const messages: Record<string, string> = {
      PLATFORM_LOGIN_REQUIRED: "平台登录状态已失效，请重新登录",
      PLATFORM_SESSION_INVALID: "平台登录状态已失效，请重新登录",
      PLATFORM_OWNER_REQUIRED: "仅平台总管理员可以管理子账户",
      PLATFORM_PERMISSION_REQUIRED: "当前账户没有该模块权限",
      PLATFORM_SUBACCOUNT_NOT_FOUND: "该子账户不存在或已失效",
      PLATFORM_MOBILE_EXISTS: "该手机号已关联其他平台账户",
      PLATFORM_CREDENTIAL_UNAVAILABLE: "当前登录凭证不可用，请先重置密码",
      PLATFORM_SUBACCOUNT_INPUT_INVALID: "姓名或手机号格式不正确",
      INSTITUTION_NOT_FOUND: "该机构账户不存在或已失效",
      INSTITUTION_CREDIT_CODE_EXISTS: "该统一社会信用代码已开户",
      CUSTOMER_MOBILE_EXISTS: "该手机号已关联其他客户账号",
      INSTITUTION_CREDENTIAL_UNAVAILABLE: "登录凭证不可用，请先重置密码",
      INSTITUTION_INPUT_INVALID: "机构账户信息格式不正确",
      INSTITUTION_RECHARGE_INPUT_INVALID: "充值金额或备注格式不正确",
      PROPERTY_ORDER_NOT_FOUND: "该不动产订单不存在",
      PROPERTY_UPLOAD_NOT_FOUND: "上传记录不存在",
      PROPERTY_UPLOAD_DUPLICATE: "相同文件已经上传",
      PROPERTY_UPLOAD_TOO_LARGE: "文件不能超过 10 MB",
      PROPERTY_ORDER_INVALID_STATE: "当前订单状态不能执行此操作",
      INVALID_HEADERS: "Excel 表头与不动产结果模板不一致",
      SUBJECT_MISMATCH: "Excel 中的姓名或身份证号与订单不一致",
      EXCEPTION_ORDER_NOT_FOUND: "该异常订单不存在",
      EXCEPTION_ACTION_NOT_ALLOWED: "当前状态不能执行此操作，请刷新后查看",
      EXCEPTION_INPUT_INVALID: "处理信息不完整，请检查后重试",
    };
    throw new Error(
      code === "PRODUCT_CONFIGURATION_INVALID" && typeof detailMessage === "string"
        ? detailMessage
        : messages[code] ?? "操作失败，请稍后重试",
    );
  }
  return payload as T;
}

function billingReconciliationSearch(
  params: BillingReconciliationQuery,
  includePage: boolean,
): URLSearchParams {
  const search = new URLSearchParams({
    dimension: params.dimension,
    subject: params.subject,
    date_from: params.dateFrom,
    date_to: params.dateTo,
    page_size: "10",
  });
  if (includePage) search.set("page", String(params.page ?? 1));
  if (params.productCode) search.set("product_code", params.productCode);
  return search;
}

export function listBillingReconciliationSubjects(
  dimension: ReconciliationDimension,
): Promise<BillingReconciliationOptions> {
  return platformRequest<BillingReconciliationOptions>(
    `/api/platform-admin/billing-reconciliation/options?dimension=${dimension}`,
  );
}

export function listBillingReconciliation(
  params: BillingReconciliationQuery,
): Promise<BillingReconciliationPage> {
  return platformRequest<BillingReconciliationPage>(
    `/api/platform-admin/billing-reconciliation?${billingReconciliationSearch(params, true)}`,
  );
}

export function billingReconciliationExportUrl(params: BillingReconciliationQuery): string {
  return appPath(
    `/api/platform-admin/billing-reconciliation/export?${billingReconciliationSearch(params, false)}`,
  );
}

function agentReconciliationSearch(params: AgentReconciliationQuery, includePage: boolean): URLSearchParams {
  const search = new URLSearchParams({ date_from: params.dateFrom, date_to: params.dateTo, page_size: "10" });
  if (includePage) search.set("page", String(params.page ?? 1));
  if (params.agentPlatformUserId) search.set("agent_platform_user_id", params.agentPlatformUserId);
  if (params.productCode) search.set("product_code", params.productCode);
  return search;
}

export function listAgentReconciliationOptions(agentPlatformUserId?: string): Promise<AgentReconciliationOptions> {
  const search = agentPlatformUserId ? `?agent_platform_user_id=${encodeURIComponent(agentPlatformUserId)}` : "";
  return platformRequest<AgentReconciliationOptions>(`/api/platform-admin/agent-reconciliation/options${search}`);
}

export function listAgentReconciliation(params: AgentReconciliationQuery): Promise<AgentReconciliationPage> {
  return platformRequest<AgentReconciliationPage>(`/api/platform-admin/agent-reconciliation?${agentReconciliationSearch(params, true)}`);
}

export function agentReconciliationExportUrl(params: AgentReconciliationQuery): string {
  return appPath(`/api/platform-admin/agent-reconciliation/export?${agentReconciliationSearch(params, false)}`);
}

export function getPlatformIdentity(): Promise<PlatformIdentity> {
  return platformRequest<PlatformIdentity>("/api/platform-admin/auth/me");
}

export function listLawyerVerifications(params: {
  query?: string;
  status?: LawyerVerificationStatus | "";
  page?: number;
  pageSize?: number;
} = {}): Promise<LawyerVerificationPage> {
  const search = new URLSearchParams({
    q: params.query ?? "",
    page: String(params.page ?? 1),
    page_size: String(params.pageSize ?? 10),
  });
  if (params.status) search.set("status", params.status);
  return platformRequest<LawyerVerificationPage>(
    `/api/platform-admin/lawyer-verifications?${search}`,
  );
}

export function getLawyerVerification(id: string): Promise<LawyerVerificationDetail> {
  return platformRequest<LawyerVerificationDetail>(
    `/api/platform-admin/lawyer-verifications/${id}`,
  );
}

export function approveLawyerVerification(id: string): Promise<LawyerVerificationDecision> {
  return platformRequest<LawyerVerificationDecision>(
    `/api/platform-admin/lawyer-verifications/${id}/approve`,
    { method: "POST" },
  );
}

export function rejectLawyerVerification(
  id: string,
  reason: string,
): Promise<LawyerVerificationDecision> {
  return platformRequest<LawyerVerificationDecision>(
    `/api/platform-admin/lawyer-verifications/${id}/reject`,
    { method: "POST", body: JSON.stringify({ reason }) },
  );
}

export async function getLawyerVerificationMaterial(url: string): Promise<Blob> {
  const token = typeof sessionStorage === "undefined"
    ? null
    : sessionStorage.getItem(PLATFORM_TOKEN_KEY);
  const response = await fetch(appPath(url), {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!response.ok) throw new Error("认证材料加载失败");
  return response.blob();
}

export function listPersonalLawyers(params: {
  query?: string;
  verificationStatus?: LawyerVerificationStatus | "";
  accountStatus?: PersonalLawyerAccountStatus | "";
  page?: number;
  pageSize?: number;
} = {}): Promise<PersonalLawyerAdminPage> {
  const search = new URLSearchParams({
    q: params.query ?? "",
    page: String(params.page ?? 1),
    page_size: String(params.pageSize ?? 10),
  });
  if (params.verificationStatus) {
    search.set("verification_status", params.verificationStatus);
  }
  if (params.accountStatus) search.set("account_status", params.accountStatus);
  return platformRequest<PersonalLawyerAdminPage>(
    `/api/platform-admin/personal-lawyers?${search}`,
  );
}

export function getPersonalLawyer(id: string): Promise<PersonalLawyerAdminDetail> {
  return platformRequest<PersonalLawyerAdminDetail>(
    `/api/platform-admin/personal-lawyers/${id}`,
  );
}

export function disablePersonalLawyer(
  id: string,
  reason: string,
): Promise<PersonalLawyerAdminDetail> {
  return platformRequest<PersonalLawyerAdminDetail>(
    `/api/platform-admin/personal-lawyers/${id}/disable`,
    { method: "POST", body: JSON.stringify({ reason }) },
  );
}

export function enablePersonalLawyer(
  id: string,
  reason: string,
): Promise<PersonalLawyerAdminDetail> {
  return platformRequest<PersonalLawyerAdminDetail>(
    `/api/platform-admin/personal-lawyers/${id}/enable`,
    { method: "POST", body: JSON.stringify({ reason }) },
  );
}

export function configurePersonalLawyerProduct(
  id: string,
  productCode: string,
  input: { status: InstitutionProductStatus; personal_price: string | null },
): Promise<PersonalLawyerAdminProduct> {
  return platformRequest<PersonalLawyerAdminProduct>(
    `/api/platform-admin/personal-lawyers/${id}/products/${productCode}`,
    { method: "PUT", body: JSON.stringify(input) },
  );
}

export function adjustPersonalLawyerBalance(
  id: string,
  input: { bucket: "RECHARGE" | "REWARD"; amount_delta: string; reason: string },
): Promise<{ recharge_balance: string; reward_balance: string }> {
  return platformRequest(`/api/platform-admin/personal-lawyers/${id}/balance-adjustments`, {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export function listPlatformSupport(params: {
  bucket: "open" | "closed";
  query?: string;
  page?: number;
  pageSize?: number;
}): Promise<PlatformSupportPage> {
  const search = new URLSearchParams({
    bucket: params.bucket,
    q: params.query ?? "",
    page: String(params.page ?? 1),
    page_size: String(params.pageSize ?? 10),
  });
  return platformRequest<PlatformSupportPage>(`/api/platform-admin/support?${search}`);
}

export function getPlatformSupport(ticketId: string): Promise<PlatformSupportDetail> {
  return platformRequest<PlatformSupportDetail>(`/api/platform-admin/support/${ticketId}`);
}

export function replyPlatformSupport(
  ticketId: string,
  message: string,
  images: File[],
): Promise<PlatformSupportDetail> {
  const body = new FormData();
  body.append("body", message);
  images.forEach((image) => body.append("images", image));
  return platformRequest<PlatformSupportDetail>(
    `/api/platform-admin/support/${ticketId}/messages`,
    { method: "POST", body },
  );
}

export function closePlatformSupport(ticketId: string): Promise<PlatformSupportDetail> {
  return platformRequest<PlatformSupportDetail>(
    `/api/platform-admin/support/${ticketId}/close`,
    { method: "POST" },
  );
}

export async function getPlatformSupportAttachment(url: string): Promise<Blob> {
  const token = typeof sessionStorage === "undefined" ? null : sessionStorage.getItem(PLATFORM_TOKEN_KEY);
  const response = await fetch(appPath(url), {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!response.ok) throw new Error("图片加载失败");
  return response.blob();
}

export function logoutPlatformAdmin(): Promise<void> {
  return platformRequest<void>("/api/platform-admin/auth/logout", { method: "POST" }).finally(
    clearPlatformToken,
  );
}

export function listPlatformSubaccounts(params: {
  query?: string;
  page?: number;
  pageSize?: number;
} = {}): Promise<PlatformSubaccountPage> {
  const search = new URLSearchParams({
    q: params.query ?? "",
    page: String(params.page ?? 1),
    page_size: String(params.pageSize ?? 10),
  });
  return platformRequest<PlatformSubaccountPage>(`/api/platform-admin/subaccounts?${search}`);
}

export function createPlatformSubaccount(input: {
  name: string;
  mobile: string;
  is_agent: boolean;
}): Promise<CreatedPlatformSubaccount> {
  return platformRequest<CreatedPlatformSubaccount>("/api/platform-admin/subaccounts", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export function updatePlatformSubaccount(
  id: string,
  input: { name: string; mobile: string; is_agent: boolean },
): Promise<PlatformSubaccount> {
  return platformRequest<PlatformSubaccount>(`/api/platform-admin/subaccounts/${id}`, {
    method: "PATCH",
    body: JSON.stringify(input),
  });
}

export function updatePlatformSubaccountPermissions(
  id: string,
  permissions: PlatformPermission[],
): Promise<PlatformSubaccount> {
  return platformRequest<PlatformSubaccount>(
    `/api/platform-admin/subaccounts/${id}/permissions`,
    {
      method: "PUT",
      body: JSON.stringify({ permissions }),
    },
  );
}

export function getPlatformSubaccountCredentials(id: string): Promise<PlatformCredentials> {
  return platformRequest<PlatformCredentials>(
    `/api/platform-admin/subaccounts/${id}/credentials`,
    { method: "POST" },
  );
}

export function resetPlatformSubaccountPassword(id: string): Promise<PlatformCredentials> {
  return platformRequest<PlatformCredentials>(
    `/api/platform-admin/subaccounts/${id}/reset-password`,
    { method: "POST" },
  );
}

export function disablePlatformSubaccount(id: string): Promise<PlatformSubaccount> {
  return platformRequest<PlatformSubaccount>(
    `/api/platform-admin/subaccounts/${id}/disable`,
    { method: "POST" },
  );
}

export function enablePlatformSubaccount(id: string): Promise<PlatformSubaccount> {
  return platformRequest<PlatformSubaccount>(
    `/api/platform-admin/subaccounts/${id}/enable`,
    { method: "POST" },
  );
}

export function listInstitutions(params: {
  query?: string;
  organizationType?: InstitutionType | "";
  status?: InstitutionStatus | "";
  agentBinding?: InstitutionAgentBinding | "";
  page?: number;
  pageSize?: number;
} = {}): Promise<InstitutionPage> {
  const search = new URLSearchParams({
    q: params.query ?? "",
    page: String(params.page ?? 1),
    page_size: String(params.pageSize ?? 10),
  });
  if (params.organizationType) search.set("organization_type", params.organizationType);
  if (params.status) search.set("status", params.status);
  if (params.agentBinding) search.set("agent_binding", params.agentBinding);
  return platformRequest<InstitutionPage>(`/api/platform-admin/institutions?${search}`);
}

export async function listAssignableInstitutionAgents(): Promise<AgentSummary[]> {
  const result = await platformRequest<{ items: AgentSummary[] }>(
    "/api/platform-admin/institutions/agents",
  );
  return result.items;
}

export function setInstitutionAgent(
  id: string,
  agentPlatformUserId: string | null,
): Promise<Institution> {
  return platformRequest<Institution>(`/api/platform-admin/institutions/${id}/agent`, {
    method: "PUT",
    body: JSON.stringify({ agent_platform_user_id: agentPlatformUserId }),
  });
}

export function createInstitution(input: CreateInstitutionInput): Promise<CreatedInstitution> {
  return platformRequest<CreatedInstitution>("/api/platform-admin/institutions", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export function updateInstitution(id: string, input: UpdateInstitutionInput): Promise<Institution> {
  return platformRequest<Institution>(`/api/platform-admin/institutions/${id}`, {
    method: "PATCH",
    body: JSON.stringify(input),
  });
}

export function getInstitutionCredentials(id: string): Promise<InstitutionCredentials> {
  return platformRequest<InstitutionCredentials>(
    `/api/platform-admin/institutions/${id}/credentials`,
    { method: "POST" },
  );
}

export function resetInstitutionPassword(id: string): Promise<InstitutionCredentials> {
  return platformRequest<InstitutionCredentials>(
    `/api/platform-admin/institutions/${id}/reset-password`,
    { method: "POST" },
  );
}

export function disableInstitution(id: string): Promise<Institution> {
  return platformRequest<Institution>(`/api/platform-admin/institutions/${id}/disable`, {
    method: "POST",
  });
}

export function enableInstitution(id: string): Promise<Institution> {
  return platformRequest<Institution>(`/api/platform-admin/institutions/${id}/enable`, {
    method: "POST",
  });
}

export function rechargeInstitution(
  id: string,
  input: { amount: string; note?: string },
): Promise<InstitutionRecharge> {
  return platformRequest<InstitutionRecharge>(
    `/api/platform-admin/institutions/${id}/recharges`,
    { method: "POST", body: JSON.stringify(input) },
  );
}

export function listInstitutionRecharges(
  id: string,
  page = 1,
  pageSize = 10,
): Promise<InstitutionRechargePage> {
  return platformRequest<InstitutionRechargePage>(
    `/api/platform-admin/institutions/${id}/recharges?page=${page}&page_size=${pageSize}`,
  );
}

export function listInstitutionProducts(
  id: string,
  params: { query?: string; moduleCode?: string; status?: InstitutionProductStatus | ""; page?: number } = {},
): Promise<InstitutionProductPage> {
  const search = new URLSearchParams({
    q: params.query ?? "",
    page: String(params.page ?? 1),
    page_size: "5",
  });
  if (params.moduleCode) search.set("module_code", params.moduleCode);
  if (params.status) search.set("status", params.status);
  return platformRequest<InstitutionProductPage>(
    `/api/platform-admin/institutions/${id}/products?${search}`,
  );
}

export function previewInstitutionProductChanges(
  id: string,
  changes: InstitutionProductChange[],
): Promise<InstitutionProductPreview> {
  return platformRequest<InstitutionProductPreview>(
    `/api/platform-admin/institutions/${id}/products/preview`,
    { method: "POST", body: JSON.stringify({ changes }) },
  );
}

export function confirmInstitutionProductChanges(
  id: string,
  changes: InstitutionProductChange[],
  previewToken: string,
): Promise<{ diff: InstitutionProductDiff }> {
  return platformRequest<{ diff: InstitutionProductDiff }>(
    `/api/platform-admin/institutions/${id}/products/confirm`,
    { method: "POST", body: JSON.stringify({ changes, preview_token: previewToken }) },
  );
}

export function listPropertyOrders(params: PropertyOrderQuery): Promise<PropertyOrderPage> {
  const search = new URLSearchParams({
    bucket: params.bucket,
    q: params.query ?? "",
    page: String(params.page ?? 1),
    page_size: String(params.pageSize ?? 10),
  });
  return platformRequest<PropertyOrderPage>(`/api/platform-admin/property-orders?${search}`);
}

export function uploadPropertyWorkbook(orderId: string, file: File): Promise<PropertyUpload> {
  const body = new FormData();
  body.append("file", file);
  return platformRequest<PropertyUpload>(
    `/api/platform-admin/property-orders/${orderId}/uploads`,
    { method: "POST", body },
  );
}

export function getPropertyUploadPreview(
  orderId: string,
  uploadId: string,
): Promise<PropertyReport> {
  return platformRequest<PropertyReport>(
    `/api/platform-admin/property-orders/${orderId}/uploads/${uploadId}/preview`,
  );
}

export function confirmPropertyUpload(
  orderId: string,
  uploadId: string,
): Promise<PropertyResultVersion> {
  return platformRequest<PropertyResultVersion>(
    `/api/platform-admin/property-orders/${orderId}/uploads/${uploadId}/confirm`,
    { method: "POST" },
  );
}

export function confirmNoProperty(
  orderId: string,
  idempotencyKey: string,
): Promise<PropertyResultVersion> {
  return platformRequest<PropertyResultVersion>(
    `/api/platform-admin/property-orders/${orderId}/confirm-no-property`,
    { method: "POST", body: JSON.stringify({ idempotency_key: idempotencyKey }) },
  );
}

export function getPropertyOrderHistory(orderId: string): Promise<PropertyOrderHistory> {
  return platformRequest<PropertyOrderHistory>(
    `/api/platform-admin/property-orders/${orderId}/history`,
  );
}

export function listExceptionOrders(params: {
  bucket: "pending" | "completed";
  query?: string;
  productCode?: string;
  page?: number;
  pageSize?: number;
}): Promise<ExceptionOrderPage> {
  const search = new URLSearchParams({
    bucket: params.bucket,
    q: params.query ?? "",
    page: String(params.page ?? 1),
    page_size: String(params.pageSize ?? 10),
  });
  if (params.productCode) search.set("product_code", params.productCode);
  return platformRequest<ExceptionOrderPage>(`/api/platform-admin/exception-orders?${search}`);
}

export function listUnifiedExceptionCases(params: {
  bucket: "pending" | "completed";
  query?: string;
  productCode?: string;
  page?: number;
  pageSize?: number;
}): Promise<UnifiedExceptionCasePage> {
  const search = new URLSearchParams({
    bucket: params.bucket,
    q: params.query ?? "",
    page: String(params.page ?? 1),
    page_size: String(params.pageSize ?? 10),
  });
  if (params.productCode) search.set("product_code", params.productCode);
  return platformRequest<UnifiedExceptionCasePage>(
    `/api/platform-admin/exception-orders/cases?${search}`,
  );
}

export function getExceptionOrder(orderId: string): Promise<ExceptionOrderDetail> {
  return platformRequest<ExceptionOrderDetail>(
    `/api/platform-admin/exception-orders/${orderId}`,
  );
}

export function requeryExceptionTask(
  taskId: string,
  idempotencyKey: string,
): Promise<ExceptionActionResult> {
  return platformRequest<ExceptionActionResult>(
    `/api/platform-admin/exception-orders/tasks/${taskId}/requery`,
    { method: "POST", body: JSON.stringify({ idempotency_key: idempotencyKey }) },
  );
}

export function confirmExceptionTimeout(
  taskId: string,
  input: { isBilled: boolean; note: string; idempotencyKey: string },
): Promise<ExceptionActionResult> {
  return platformRequest<ExceptionActionResult>(
    `/api/platform-admin/exception-orders/tasks/${taskId}/confirm-timeout`,
    {
      method: "POST",
      body: JSON.stringify({
        is_billed: input.isBilled,
        note: input.note,
        idempotency_key: input.idempotencyKey,
      }),
    },
  );
}

export function confirmExceptionNoData(
  taskId: string,
  note: string,
  idempotencyKey: string,
): Promise<ExceptionActionResult> {
  return platformRequest<ExceptionActionResult>(
    `/api/platform-admin/exception-orders/tasks/${taskId}/confirm-no-data`,
    {
      method: "POST",
      body: JSON.stringify({ note, idempotency_key: idempotencyKey }),
    },
  );
}

export function getPaymentCollectionSummary(): Promise<PaymentCollectionSummary> {
  return platformRequest<PaymentCollectionSummary>(
    "/api/platform-admin/payment-collections/summary",
  );
}

export function getPaymentCollectionReadiness(): Promise<PaymentCollectionReadiness> {
  return platformRequest<PaymentCollectionReadiness>(
    "/api/platform-admin/payment-collections/readiness",
  );
}

export function listPaymentCollections(params: {
  query?: string;
  status?: PaymentCollectionStatus | "";
  channel?: PaymentCollectionChannel | "";
  dateFrom?: string;
  dateTo?: string;
  page?: number;
  pageSize?: number;
}): Promise<PaymentCollectionPage> {
  const search = new URLSearchParams({
    q: params.query ?? "",
    page: String(params.page ?? 1),
    page_size: String(params.pageSize ?? 10),
  });
  if (params.status) search.set("status", params.status);
  if (params.channel) search.set("channel", params.channel);
  if (params.dateFrom) search.set("date_from", params.dateFrom);
  if (params.dateTo) search.set("date_to", params.dateTo);
  return platformRequest<PaymentCollectionPage>(
    `/api/platform-admin/payment-collections/orders?${search}`,
  );
}

export function getPaymentCollectionDetail(orderId: string): Promise<PaymentCollectionDetail> {
  return platformRequest<PaymentCollectionDetail>(
    `/api/platform-admin/payment-collections/orders/${encodeURIComponent(orderId)}`,
  );
}

export interface CollectionEntryQuery {
  query?: string;
  accountType?: CollectionAccountType | "";
  method?: CollectionMethod | "";
  dateFrom?: string;
  dateTo?: string;
  page?: number;
  pageSize?: number;
}

function collectionEntrySearch(params: CollectionEntryQuery, includePage: boolean): URLSearchParams {
  const search = new URLSearchParams({ q: params.query ?? "" });
  if (includePage) {
    search.set("page", String(params.page ?? 1));
    search.set("page_size", String(params.pageSize ?? 10));
  }
  if (params.accountType) search.set("account_type", params.accountType);
  if (params.method) search.set("method", params.method);
  if (params.dateFrom) search.set("date_from", params.dateFrom);
  if (params.dateTo) search.set("date_to", params.dateTo);
  return search;
}

export function listCollectionEntries(params: CollectionEntryQuery): Promise<CollectionEntryPage> {
  return platformRequest<CollectionEntryPage>(
    `/api/platform-admin/payment-collections/entries?${collectionEntrySearch(params, true)}`,
  );
}

export function collectionEntriesExportUrl(params: CollectionEntryQuery): string {
  return `/api/platform-admin/payment-collections/export?${collectionEntrySearch(params, false)}`;
}
